import { MAIN_ROUTES } from './../../routing/constants/mainRoutes';
import { Invites, IInviteService, ITable } from './interfaces';
import { Subject } from 'rxjs';
import { IModalProvider } from './../../modal/model/interfaces';
import { IPlayersOnlineRepository } from './../../player/model/interfaces';
import { MessagesService } from './../../chat/messagesService';
import { ISocketClient } from './../../socket/interfaces';
import { History } from 'history';
import { t } from '@base/utils/helpers';
import { BasePreferences } from '@base/features/preferences/model/types';
import BaseTable from './baseTable';
import BufferReader from '../../socket/bufferReader';
import SoundProvider from './soundProvider';
import Codes from '../../socket/codes';
import Utils from '../../socket/utils';
import BaseTablesRepository from './baseTableRepository';
import { ITicketService } from '@base/features/tickets/model/interfaces';

class InviteService<P extends BasePreferences = BasePreferences, T extends ITable = BaseTable>
  implements IInviteService {
  private invites: Record<string, T> = {};
  onInvitesChange = new Subject<Invites>();
  doNotDisturb = false;

  constructor(
    private socket: ISocketClient,
    private messagesService: MessagesService<P, T>,
    private playersOnlineRepository: IPlayersOnlineRepository<P, T>,
    private modalProvider: IModalProvider,
    private soundProvider: SoundProvider<P>,
    private tableService: BaseTablesRepository<T>,
    private history: History,
    private ticketService: ITicketService
  ) {}

  private onReceiveInvite(data: BufferReader) {
    const invite = {
      type: data.readUint8(),
      tid: data.readInt16(),
    };
    this.onGotInvite(invite);
  }

  private onSendInvite(pid: number, tid: number, inviteType: any): void {
    const data = [Codes.Types.CP_INVITE]
      .concat(Utils.decToInt8(inviteType))
      .concat(Utils.decToInt16(pid))
      .concat(Utils.decToInt16(tid));
    this.socket.sendPacket(data);
  }

  private goToThePage(path: string) {
    this.history.push(path);
  }

  get sound() {
    return this.soundProvider;
  }

  onGotInvite(invite: { tid: number; type: number }): void {
    const error = this.playersOnlineRepository.checkParams(invite);
    if (error) return console.error('Error: onGotInvite', error, invite);

    const table = this.tableService.getTable(invite.tid);
    if (!table || !this.playersOnlineRepository.me) return;

    if (this.doNotDisturb && invite.type !== Codes.InviteType.DECLINE) {
      this.declineInvite(invite.tid);
      return;
    }

    if (invite.type === Codes.InviteType.DECLINE) {
      this.messagesService.createChatMessage({
        type: Codes.MessageTypes.TABLE,
        text: t('chat_your_invitation_declined'),
        tid: table.tid,
      });
      return;
    }

    if (table.isAllPlaying()) return;

    if (
      (!this.playersOnlineRepository.me?.isMyTable(table) &&
        this.playersOnlineRepository.me?.isNotPlayer(table)) ||
      invite.type === Codes.InviteType.IND
    ) {
      this.addInvite(table as T);

      if (invite.type === Codes.InviteType.ALL) {
        const him = table.getOwner();
        const tableId = table.tid;
        if (him && !him.isIgnored() && him.checkInviteMessageInterval(tableId)) {
          this.messagesService.createChatMessage({
            type: Codes.MessageTypes.ALL,
            pid: him.getPid(),
            text: t('please_join'),
            tid: tableId,
          });
        }
      }
    }
  }

  addInvite(table: T): void {
    if (table.tid !== 0) {
      const id = String(table.tid);
      if (Object.prototype.hasOwnProperty.call(this.invites, id)) return;
      const newInvite = { [id]: table };
      Object.assign(this.invites, newInvite);
      this.onInvitesChange.next({ ...this.invites });
      // Не проигрывать звук если я за столом
      if (this.playersOnlineRepository.me?.getTable()) return;
      this.sound.play(this.sound.codes.INFO, this.sound.categories.INVITATION);
    }
  }

  checkInvite(table: T): boolean {
    if (!table) return false;
    return Boolean(this.invites[table.tid]);
  }

  getInvites(): T[] {
    const hasPlayers = (table: T): boolean => {
      return Boolean(table.getAnyPlayer());
    };
    const invites = [];
    for (const tid in this.invites) {
      if (Object.prototype.hasOwnProperty.call(this.invites, tid) && hasPlayers(this.invites[tid])) {
        invites.push(this.invites[tid]);
      }
    }
    return invites;
  }

  removeInvites(table: T | undefined): void {
    if (table instanceof BaseTable && !this.checkInvite(table as T)) return;
    if (table?.tid) {
      delete this.invites[table.tid];
      this.onInvitesChange.next({ ...this.invites });
    }
  }

  sendInvite(pid: number, tid: number, inviteType: number): void {
    this.onSendInvite(pid, tid, inviteType);
  }

  sendDeclineInvite(table: T) {
    const owner = table.getOwner();
    if (owner && owner.id && table.tid) {
      this.onSendInvite(owner.id, table.tid, Codes.InviteType.DECLINE);
    }
  }

  //Отклоняем все инвайты
  declineAllInvites() {
    const ids = Object.keys(this.invites);

    for (let i = 0; i < ids.length; i++) {
      const table = this.invites[ids[i]];
      this.sendDeclineInvite(table);
      this.removeInvites(table);
    }
  }

  //Отклоняем 1 инвайт
  declineInvite(tableId: number) {
    const table = this.tableService.getTable(tableId);
    if (!table || !this.invites[tableId]) return false;
    this.sendDeclineInvite(table as T);
    this.removeInvites(table as T);
  }

  acceptInvite(tableId: number, eulaAccepted: boolean): boolean {
    const table = this.tableService.getTable(tableId);

    if (!this.playersOnlineRepository.me || this.playersOnlineRepository.me.getTable()) {
      this.modalProvider.show({
        title: t('warning'),
        body: t('too_many_tables_for_one_player'),
        buttons: [
          {
            title: t('ok'),
            onClick: () => {
              this.modalProvider.hide();
              this.goToThePage(MAIN_ROUTES.game);
            },
          },
        ],
      });
      return false;
    }

    const tableNumber = String(table?.getNumber());
    const title = t('join_table_error_1s_2s', t('join_l'), tableNumber);
    if (!table) {
      //если нет стола
      this.modalProvider.show({
        title,
        body: t('table_not_found'),
        buttons: [{ title: t('ok'), onClick: () => this.modalProvider.hide() }],
      });
      return false;
    } else if (table.isAllPlaying()) {
      // Если все игроки уже присутствуют
      this.modalProvider.show({
        title,
        body: t('table_full'),
        buttons: [{ title: t('ok'), onClick: () => this.modalProvider.hide() }],
      });
      return false;
    } else if (table.getCost() > 0) {
      if (!this.ticketService.haveSufficientBalance(table)) return false;
    }

    if (table.getCost() > 0 && !eulaAccepted) {
      this.modalProvider.showTermsAndConditions(tableId);
      return false;
    } else {
      // FIXME: Этот метод реализован в lobbyService.joinTable....
      // Мы не можем прокинуть lobbyService в inviteService
      // Надо вынести joinTable
      // JOIN TABLE
      const data = [Codes.Types.CP_TABLE_JOIN]
        .concat(Utils.decToInt16(tableId))
        .concat(Utils.int8ToUint8(Codes.JoinMode.INVITED));
      this.socket.sendPacket(data);
      /// END JOIN TABLE
      return true;
    }
  }

  getDisturbStatus(): boolean {
    return this.doNotDisturb;
  }

  setDisturbStatus(status: boolean): void {
    this.doNotDisturb = status;
  }
}

export default InviteService;
